import { combineReducers } from 'redux';

import { cast, isValidUrl } from 'redpoints-front-common/helpers';

import filters, * as fromFiltersData from './filters';
import * as fromAssetsData from './filters/assets';
import * as fromDomainsData from './filters/domains';
import * as fromIncidentStatusData from './filters/incidentStatus';
import * as fromLinkStatusData from './filters/linkStatus';
import * as fromNonEnforcementReasons from './filters/nonEnforcementReasons';
import * as fromProtectionTypes from './filters/protectionTypes';
import * as fromValues from './filters/values';
import table from './table';
import * as fromTableExportBrandPermission from './table/tableExportBrandPermission';
import * as fromTableExportPiracyPermission from './table/tableExportPiracyPermission';

const allIncidents = combineReducers({
  filters,
  table,
});

export default allIncidents;

export const getAllIncidentsFiltersAssetSelected = state =>
  state.reporting.allIncidents.filters.values.assetId;

export const getAllIncidentsFiltersProtectionTypeSelected = state =>
  state.reporting.allIncidents.filters.values.protectionType;

export const getAllIncidentsFiltersDeindexationSelected = state =>
  state.reporting.allIncidents.filters.values.deindexation;

export const getAllIncidentsFiltersDomainsSelected = state =>
  state.reporting.allIncidents.filters.values.domainId;

export const getAllIncidentsFiltersLabelSelected = state =>
  fromValues.getAllIncidentsFiltersLabelSelected(
    state.reporting.allIncidents.filters.values
  );

export const getAllIncidentsFiltersDetectionDate = state => {
  const {
    detectionDateFrom,
    detectionDateTo,
  } = state.reporting.allIncidents.filters.values;
  return cast.stringsToDates([detectionDateFrom, detectionDateTo]);
};

export const getAllIncidentsFiltersConfirmationDate = state => {
  const {
    confirmationDateFrom,
    confirmationDateTo,
  } = state.reporting.allIncidents.filters.values;
  return cast.stringsToDates([confirmationDateFrom, confirmationDateTo]);
};

export const getAllIncidentsFiltersEnforcementDate = state => {
  const {
    enforcementDateFrom,
    enforcementDateTo,
  } = state.reporting.allIncidents.filters.values;
  return cast.stringsToDates([enforcementDateFrom, enforcementDateTo]);
};

export const getAllIncidentsFiltersClosureDate = state => {
  const {
    closureDateFrom,
    closureDateTo,
  } = state.reporting.allIncidents.filters.values;
  return cast.stringsToDates([closureDateFrom, closureDateTo]);
};

export const getAllIncidentsFiltersEnforcementStatusSelected = state =>
  state.reporting.allIncidents.filters.values.enforcementStatus;

export const getAllIncidentsFiltersEnforcementStatusGroupSelected = state =>
  state.reporting.allIncidents.filters.values.enforcementStatusGroupId;

export const getAllIncidentsFiltersIncidentStatusSelected = state =>
  state.reporting.allIncidents.filters.values.incidentStatus;

export const getAllIncidentsFiltersIncidentStatusGroupSelected = state =>
  state.reporting.allIncidents.filters.values.incidentStatusGroupId;

export const getAllIncidentsFiltersLevelStatusData = state => {
  const { incidents, links } = state.reporting.allIncidents.filters;

  return {
    incidents,
    links,
  };
};

export const getAllIncidentsFiltersIncidentStatusTreeData = state =>
  fromIncidentStatusData.getAllIncidentsFiltersIncidentStatusTreeData(
    state.reporting.allIncidents.filters.incidents
  );

export const getAllIncidentsFiltersLinkStatusSelected = state =>
  state.reporting.allIncidents.filters.values.linkStatusGroup;

export const getAllIncidentsFiltersLinkStatusTreeData = state =>
  fromLinkStatusData.getAllIncidentsFiltersLinkStatusTreeData(
    state.reporting.allIncidents.filters.links
  );

export const getAllIncidentsFiltersNonEnforcementReasonNormalizedNamesFromSelectedCodes = state => {
  const reasonCode = getAllIncidentsFiltersNonEnforcementReasonsCodesSelected(
    state
  );
  const normalizedNames = fromNonEnforcementReasons.getAllIncidentsFiltersNonEnforcementReasonNormalizedNamesFromCodes(
    state.reporting.allIncidents.filters.nonEnforcementReasons,
    reasonCode
  );

  return normalizedNames;
};

export const getAllIncidentsFiltersNonEnforcementReasonsCodesSelected = state =>
  state.reporting.allIncidents.filters.values.reasonCode;

export const getAllIncidentsFiltersNonEnforcementReasonsGroupedByName = state =>
  fromNonEnforcementReasons.getAllIncidentsFiltersNonEnforcementReasonsGroupedByName(
    state.reporting.allIncidents.filters.nonEnforcementReasons
  );

export const getAllIncidentsFiltersNonEnforcementReasonsIsLoading = state =>
  state.reporting.allIncidents.filters.nonEnforcementReasons.isLoading;

export const getAllIncidentsFiltersSearchUrl = state =>
  state.reporting.allIncidents.filters.values.url;

export const getAllIncidentsFiltersSearchLinkUrl = state =>
  state.reporting.allIncidents.filters.values.linkUrl || undefined;

export const getAllIncidentsAssetsIsLoading = state =>
  state.reporting.allIncidents.filters.assets.isLoading;

export const getAllIncidentsAssetsResponse = (state, { reportType }) => {
  const { assets } = state.reporting.allIncidents.filters;

  return reportType === 'all-incidents'
    ? fromAssetsData.getAllIncidentsAssetsResponseInternal(assets)
    : fromAssetsData.getAllIncidentsAssetsResponse(assets);
};

export const getAllIncidentsDomainsIsLoading = state =>
  state.reporting.allIncidents.filters.domains.isLoading;

export const getAllIncidentsFiltersIsLoading = state =>
  state.reporting.allIncidents.filters.assets.isLoading ||
  state.reporting.allIncidents.filters.incidents.isLoading ||
  state.reporting.allIncidents.filters.links.isLoading ||
  state.reporting.allIncidents.filters.protectionTypes.isLoading;

export const getAllIncidentsDomainsResponse = state =>
  fromDomainsData.getAllIncidentsDomainsResponse(
    state.reporting.allIncidents.filters.domains
  );

export const getAllIncidentsFiltersPriorities = state =>
  state.reporting.allIncidents.filters.values.priorityId;

export const getAllIncidentsProtectionTypesResponse = state =>
  fromProtectionTypes.getAllIncidentsProtectionTypesResponse(
    state.reporting.allIncidents.filters.protectionTypes
  );

export const getAllIncidentsApplyIsDisabled = state => {
  const { assets, domains, values } = state.reporting.allIncidents.filters;
  const isLoadingIncidents =
    state.reporting.allIncidents.table.incidents.isLoading;
  const isLoading = assets.isLoading || domains.isLoading || isLoadingIncidents;
  const linkUrl = values['linkUrl'];
  const isLinkUrlValid = isValidUrl(linkUrl) || !linkUrl;

  const listOfMandatoryKeys = [
    'detectionDateFrom',
    'detectionDateTo',
    'confirmationDateFrom',
    'confirmationDateTo',
    'enforcementDateFrom',
    'enforcementDateTo',
    'closureDateFrom',
    'closureDateTo',
    'linkUrl',
  ];

  const areAllMandatoryFieldsEmpty = listOfMandatoryKeys.reduce(
    (acum, currentKey) => acum && Boolean(values[currentKey]) === false,
    true
  );

  return isLoading || areAllMandatoryFieldsEmpty || !isLinkUrlValid;
};

export const getAllIncidentsClearIsDisabled = state =>
  state.reporting.allIncidents.filters.assets.isLoading ||
  state.reporting.allIncidents.filters.domains.isLoading;

export const getAllIncidentsFilters = state => {
  return fromFiltersData.getAllIncidentsFilters(
    state.reporting.allIncidents.filters
  );
};

export const getAllIncidentsTableData = state =>
  state.reporting.allIncidents.table.incidents.response.data;

export const getAllIncidentsTableIsLoading = state =>
  getAllIncidentsTableExportIsLoading(state) ||
  state.reporting.allIncidents.table.incidents.isLoading;

export const getAllIncidentsTableExportIsLoading = state =>
  state.reporting.allIncidents.table.tableExportActions.isLoading;

export const getAllIncidentsTableHasExportBrandPermission = state =>
  fromTableExportBrandPermission.getAllIncidentsTableHasExportBrandPermission(
    state.reporting.allIncidents.table.tableExportBrandPermission
  );

export const getAllIncidentsTableHasExportPiracyPermission = state =>
  fromTableExportPiracyPermission.getAllIncidentsTableHasExportPiracyPermission(
    state.reporting.allIncidents.table.tableExportPiracyPermission
  );

export const getAllIncidentsTableActionsBookmarkStatus = (
  state,
  incidentId
) => {
  const bookmarkStatus =
    state.reporting.allIncidents.table.tableActionsBookmarkStatus;
  return bookmarkStatus && bookmarkStatus.size
    ? bookmarkStatus.has(incidentId)
    : false;
};
